import React, { useState, useContext } from "react";
import { useNavigate } from "react-router-dom";
import { API, AuthContext } from "../App";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { toast } from "sonner";
import { HardHat, Loader2 } from "lucide-react";

export default function DemoCreateSite() {
  const { user, refreshUser } = useContext(AuthContext);
  const navigate = useNavigate();
  const [name, setName] = useState("");
  const [address, setAddress] = useState("");
  const [saving, setSaving] = useState(false);

  const create = async () => {
    if (!name.trim() || !address.trim()) {
      toast.error("Site name and address are required");
      return;
    }
    setSaving(true);
    try {
      const res = await fetch(`${API}/sites`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ name, address }),
      });
      if (!res.ok) {
        const e = await res.json().catch(() => ({}));
        throw new Error(e.detail || "Could not create site");
      }
      await refreshUser();
      toast.success("Site created — welcome aboard!");
      navigate("/dashboard");
    } catch (e) {
      toast.error(e.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-amber-50 to-orange-50 flex items-center justify-center px-4" data-testid="demo-create-site-page">
      <div className="w-full max-w-md bg-white rounded-2xl shadow-xl border border-amber-100 p-7">
        <div className="flex items-center gap-3 mb-5">
          <div className="w-11 h-11 rounded-xl bg-amber-500 flex items-center justify-center">
            <HardHat className="w-6 h-6 text-white" />
          </div>
          <div>
            <h1 className="text-xl font-bold text-slate-900">First — let's set up your site</h1>
          </div>
        </div>
        <p className="text-sm text-slate-500 mb-5">
          You can manage one site during your trial. Give it a name and address to get started.
        </p>

        <div className="space-y-4">
          <div>
            <Label>Site name *</Label>
            <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. 12 Smith Street Build" data-testid="demo-site-name" />
          </div>
          <div>
            <Label>Site address *</Label>
            <Input value={address} onChange={(e) => setAddress(e.target.value)} placeholder="Street, suburb, state" data-testid="demo-site-address" />
          </div>
          <div>
            <Label>Site manager</Label>
            <Input value={user?.username || "You"} disabled className="bg-slate-100" data-testid="demo-site-manager" />
            <p className="text-xs text-slate-400 mt-1">Pre-assigned to you for your trial.</p>
          </div>
          <Button onClick={create} disabled={saving} className="w-full h-11 bg-amber-500 hover:bg-amber-600 font-semibold" data-testid="demo-create-site-btn">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Create My Site"}
          </Button>
          <p className="text-xs text-center text-slate-400">It only takes 30 seconds.</p>
        </div>
      </div>
    </div>
  );
}
